import { ArrowUpRight } from "lucide-react";
import { useBrain } from "@/contexts/ui/brain";
import { cn } from "@/lib/utils";

interface SmartPrompt {
  title: string;
  hint: string;
  prompt: string;
}

const MASCOT_SRC = "/genie-mascots/role/support.png";

const PROMPTS: SmartPrompt[] = [
  {
    title: "What should I add this week?",
    hint: "Top recommendations",
    prompt:
      "Look at my knowledge base and my recent conversations. Tell me the top 5 things I should add this week to make my genies handle calls better — be specific about which genie, what content to add, and why each one matters. Rank by impact.",
  },
  {
    title: "What does my genie not know yet?",
    hint: "Gaps from recent calls",
    prompt:
      "Scan my recent conversations across all genies for moments where a genie said it didn't know, couldn't find, had to check, or hesitated. Group them into themes and tell me exactly what content I should add to the knowledge base to close each gap. Be concrete — draft the first paragraph of each missing article.",
  },
  {
    title: "Which genies have thin knowledge?",
    hint: "Coverage audit",
    prompt:
      "Audit my genies by knowledge depth. Which ones have very few documents relative to how much they're being used? Which ones are missing content on topics that come up frequently in their conversations? Rank them by how urgently they need more knowledge.",
  },
  {
    title: "Which documents matter most?",
    hint: "High-value content",
    prompt:
      "Across my knowledge base, tell me which documents are the most-used or most-cited by my genies in real conversations. Which ones do my genies lean on the hardest? Flag any documents that look high-value but are out of date.",
  },
  {
    title: "What questions keep coming up?",
    hint: "Recurring asks",
    prompt:
      "Cluster the questions my callers ask most often across all conversations. For each cluster, tell me whether my knowledge base already has a good answer or if I need to add one. Draft the missing answers in 2-3 sentences each.",
  },
  {
    title: "Are any documents contradicting?",
    hint: "Quality audit",
    prompt:
      "Look across my knowledge base for contradictions, stale content, or duplicate information that might confuse a genie. Tell me which specific documents conflict, what they disagree about, and which one I should keep, update, or merge.",
  },
  {
    title: "Draft an FAQ I'm missing",
    hint: "Content generator",
    prompt:
      "Based on the questions my callers ask most often, draft a complete FAQ document I can upload to my knowledge base. Pick the genie that needs it most, write 8-12 question-and-answer pairs in the genie's voice, and tell me which genie to attach it to.",
  },
  {
    title: "Suggest a folder structure",
    hint: "Organize my docs",
    prompt:
      "Look at all my knowledge base documents and suggest a clean folder structure to organize them. Group by topic, by genie, or by document type — whichever makes the most sense given what I have. Tell me which documents go in each folder.",
  },
];

interface KnowledgeBaseSmartPromptsProps {
  variant?: "card" | "sheet";
  onPromptOpen?: () => void;
}

export const KnowledgeBaseSmartPrompts = ({
  variant = "card",
  onPromptOpen,
}: KnowledgeBaseSmartPromptsProps = {}) => {
  const { openBrain } = useBrain();

  const handleClick = (prompt: string) => {
    openBrain(prompt);
    onPromptOpen?.();
  };

  if (variant === "sheet") {
    return (
      <div className="space-y-2">
        {PROMPTS.map((p) => (
          <button
            key={p.title}
            type="button"
            onClick={() => handleClick(p.prompt)}
            className="group w-full flex items-start justify-between gap-3 text-left rounded-xl border border-genie-border bg-white px-4 py-3 hover:border-genie-primary/40 hover:shadow-sm transition-all"
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 group-hover:bg-gradient-to-r group-hover:from-[#4E9CFF] group-hover:to-[#FF4DB8] group-hover:bg-clip-text group-hover:text-transparent transition-colors">
                {p.title}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{p.hint}</div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF4DB8] flex-shrink-0 mt-0.5" />
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
        <img src={MASCOT_SRC} alt="" className="h-6 w-6 object-contain flex-shrink-0" />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">Ask Your Knowledge Base</h3>
          <p className="text-xs text-gray-500">
            Don't know what to add? Start here — your Genie will tell you what's missing.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 p-3">
        {PROMPTS.map((p) => (
          <button
            key={p.title}
            type="button"
            onClick={() => handleClick(p.prompt)}
            className={cn(
              "group relative text-left rounded-lg border border-gray-200 hover:border-transparent hover:shadow-sm hover:shadow-genie-primary/15 transition-all px-3 py-2.5",
            )}
            title={p.hint}
          >
            <span
              className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
              style={{
                padding: "1px",
                background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
                WebkitMask:
                  "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-[#4E9CFF] group-hover:to-[#FF4DB8] group-hover:bg-clip-text group-hover:text-transparent transition-colors">
                  {p.title}
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5 truncate">{p.hint}</div>
              </div>
              <ArrowUpRight className="w-3 h-3 text-gray-300 group-hover:text-[#FF4DB8] flex-shrink-0 mt-0.5" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default KnowledgeBaseSmartPrompts;
